// src/modules/units/tyres/views/import/tyreimport-template.js
//
// The tyre import TEMPLATE: an Excel workbook laid out like the New / Edit tyre
// form, with dropdowns built from the signed-in customer's own yards, suppliers
// and tyre catalogue. Pure — data in, a workbook description out (written to
// bytes by shared/tyre-xlsx-write.js). No DOM, no fetch.
//
// ONE DEFINITION. The columns are DERIVED from the form's field config
// (NEW_TYRE_FIELDS in create/newtyreform.view.js): same fields, same order
// (Yard → Identity → Specification → Supplier & invoice), same labels. A field
// added to the form appears in the template without touching this file; the
// harness proves every template heading maps itself on import, so a form/import
// mismatch fails a test rather than a customer's upload.
//
// The workbook:
//   Tyres         the sheet to fill in — frozen header, one column per form
//                 field, typed column formats, data validations
//   Instructions  what to put in each column
//   Lists         HIDDEN: the dropdown sources (the importer skips hidden sheets)
//
// Dropdowns mirror the form's controls:
//   select (Yard, Supplier, Type, Application, Grade) → list, STOP on anything else
//   Size (a select over an open list)                → list, WARNING (can proceed)
//   typeahead (Manufacturer, Model, Tread pattern)    → list, INFORMATION (free
//       entry allowed — a new spec is checked against the catalogue on import).
//       Model is filtered to the row's manufacturer and Tread pattern to its
//       manufacturer + model, falling back to every value when there is no match,
//       exactly as the form's typeaheads narrow.
// Excel 365 offers type-to-search in these list dropdowns (its typeahead).
//
// CATALOGUE AUTO-FILL, as on the form. Picking a manufacturer + model fills, by
// formula: the tread pattern (when the model has only one), size, type,
// application, grade, initial tread, minimum tread for retread and expected
// mileage — each only where every matching catalogue entry agrees, narrowed by
// tread and then by size exactly like the form (the rule lives in
// tyre-catalogue-match.js; the template PRECOMPUTES its answers into a lookup
// table, so Excel only has to look them up). Grade falls back to "New", the
// form's default. Suggested cells show grey (conditional format: a formula that
// shows a value; empty rows keep the plain grid);
// typing over one replaces the suggestion, just as editing an auto-filled field
// does on the form. Current tread is deliberately NOT suggested: on the form a
// person sees it copied from the new tread; in a bulk sheet of used tyres a
// silent "no wear" figure would be wrong data.
// The importer recognises suggested values (formulas reading the lookup sheet,
// directly or through one of its workbook names) and, for a tyre that already
// exists, never lets one replace a recorded value.

import { NEW_TYRE_FIELDS } from '../create/newtyreform.view.js';
import { TYRE_SIZES, TYRE_TYPES, TYRE_APPLICATIONS, TYRE_GRADES } from '../../shared/tyre-vocab.js';
import {
  normaliseCatalogueValue, normaliseTyreSize, canonicalTyreSize, uniqueCatalogueValues,
  singleCatalogueTread, narrowCatalogueRows, catalogueDefaultsFor
} from '../../shared/tyre-catalogue-match.js';
import { SUGGESTION_SHEET } from '../../shared/tyre-import-file.js';
import { yardChoices, supplierChoices } from '../../shared/tyre-import-validate.js';
import { STYLE, colLetter, sheetRef } from '../../shared/tyre-xlsx-write.js';

export const TEMPLATE_SHEET = 'Tyres';
export const INSTRUCTIONS_SHEET = 'Instructions';
// The hidden lookup sheet. Its (distinctive) name is how the importer recognises a
// value that came from a catalogue-suggestion formula — see SUGGESTION_SHEET.
export const LISTS_SHEET = SUGGESTION_SHEET;
/** Rows that carry the dropdowns and formats (below the header). */
export const TEMPLATE_ROWS = 2000;

// Per-field guidance for the Instructions sheet (what the form conveys through
// placeholders, help text and behaviour). Fields not listed fall back to a
// generic line, so a new form field still gets a row.
const GUIDANCE = {
  yardId: 'Pick from the list. Leave blank to use the yard you choose when importing.',
  externalTyreId: 'Your company’s own tyre ID or reference number. Must be unique — it is how existing tyres are matched (case does not matter).',
  tyreRFID: 'The RFID tag, if the tyre has one.',
  tyreSerialNumber: 'The serial number moulded on the casing.',
  tyreBrand: 'Pick from your tyre catalogue or type a new one.',
  tyreModel: 'Lists the models of the manufacturer in this row; type a new one if needed.',
  tyreTreadPattern: 'Lists the tread patterns of the manufacturer and model in this row; type a new one if needed.',
  tyreSize: 'Pick a size. A size that is not listed is accepted after a warning.',
  tyreType: 'Pick from the list.',
  tyreApplication: 'Pick from the list.',
  tyreGrade: 'Pick from the list. New tyres are “New”.',
  initialTyreTreadDepth: 'Tread depth when the tyre was brand new — not when it was added to stock. Millimetres, e.g. 20 or 19.5.',
  minimumTreadForRetread: 'Millimetres.',
  manufacturerExpectedMileage: 'Whole kilometres.',
  tyreTreadDepth: 'The tread depth measured now, in millimetres.',
  tyreKm: 'For an existing tyre first added to stock: the kilometres it has already done. Ignored once PressureTrack tracks its distance.',
  notes: 'Anything else worth recording.',
  supplierId: 'Pick from your suppliers (Setup → Suppliers).',
  purchaseDate: 'A date, e.g. 2026-04-08.',
  invoiceNo: 'The supplier’s invoice number.',
  purchaseCost: 'Excluding VAT, in rand. Numbers only, e.g. 5035.50.'
};

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

function kindOf(f) {
  if (f.control === 'date') return 'date';
  if (f.control === 'number') return String(f.step) === '1' ? 'integer' : 'decimal';
  if (f.dynamic === 'yards') return 'yard';
  if (f.dynamic === 'suppliers') return 'supplier';
  if (f.control === 'typeahead') return `typeahead:${f.typeahead}`;
  if (f.control === 'select') return 'select';
  return 'text';
}

/**
 * The template's columns, from the form config.
 * Tyre ID is the only required column: a blank Yard falls back to the yard chosen
 * in the import wizard, so the form's "Yard is required" holds without forcing it
 * into every row.
 * @returns {Array<{key,header,label,kind,required,width,field}>}
 */
export function templateColumns(fields = NEW_TYRE_FIELDS) {
  return fields
    .filter((f) => !f.localOnly && f.control !== 'calculated')
    .map((f) => {
      const required = f.key === 'externalTyreId';
      const unit = f.unit ? ` (${f.unit})` : (f.labelNote === 'excl. VAT' ? ' (excl. VAT)' : '');
      const header = `${f.label}${unit}${required ? ' *' : ''}`;
      return {
        key: f.key,
        header,
        label: f.label,
        kind: kindOf(f),
        required,
        // Dropdown columns hold names ("Main Depot · Demo Transport"), so they
        // start wider than the heading alone would make them.
        width: Math.min(34, Math.max(['yard', 'supplier'].includes(kindOf(f)) || f.control === 'typeahead' ? 24 : 13, header.length + 3)),
        field: f
      };
    });
}

// ---------------------------------------------------------------------------
// Lists (the dropdown sources)
// ---------------------------------------------------------------------------

const byText = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

// Unique by a normaliser, keeping the first spelling seen; optionally sorted.
function uniqueBy(values, normalise = normaliseCatalogueValue, { sort = true } = {}) {
  const seen = new Map();
  for (const v of values) {
    const t = String(v ?? '').trim();
    const k = normalise(t);
    if (t && !seen.has(k)) seen.set(k, t);
  }
  const out = [...seen.values()];
  return sort ? out.sort(byText) : out;
}

/**
 * Everything the dropdowns offer.
 *
 * Catalogue: ACTIVE entries only — the same set the form's typeaheads offer.
 * Sizes / types / applications / grades: the form's lists, plus any catalogue
 * value not in them (the form adds such a value to its select when a catalogue
 * match fills it), vocabulary spellings first and order kept.
 *
 * @param {{yards?:object[], suppliers?:object[], catalogue?:object[]}} src
 */
export function buildTemplateLists({ yards = [], suppliers = [], catalogue = [] } = {}) {
  const active = (catalogue || []).filter((e) => e && e.isActive !== false);
  const extend = (base, key, normalise) => uniqueBy([...base, ...active.map((e) => e[key])], normalise, { sort: false });

  const manufacturers = uniqueBy(active.map((e) => e.manufacturerName));

  // Manufacturer → model pairs and manufacturer|model → tread triples, sorted so
  // each group is CONTIGUOUS (the dependent-list formulas take a slice by
  // MATCH + COUNTIF, both case-insensitive — so the grouping is too).
  // Keyed case-insensitively; each pair keeps the FIRST spelling seen, and its
  // treads are keyed by that same spelling, so the Lists sheet reads consistently.
  const pairs = [];
  const pairSeen = new Map();
  const triples = [];
  const tripleSeen = new Set();
  for (const e of active) {
    const m = String(e.manufacturerName ?? '').trim();
    const mo = String(e.modelName ?? '').trim();
    const t = String(e.treadPatternCode ?? '').trim();
    if (!m || !mo) continue;
    const pk = `${normaliseCatalogueValue(m)}|${normaliseCatalogueValue(mo)}`;
    if (!pairSeen.has(pk)) { const p = { manufacturer: m, model: mo }; pairSeen.set(pk, p); pairs.push(p); }
    if (t) {
      const tk = `${pk}|${normaliseCatalogueValue(t)}`;
      const p = pairSeen.get(pk);
      if (!tripleSeen.has(tk)) { tripleSeen.add(tk); triples.push({ key: `${p.manufacturer}|${p.model}`, tread: t }); }
    }
  }
  pairs.sort((a, b) => byText(a.manufacturer.toLowerCase(), b.manufacturer.toLowerCase()) || byText(a.model, b.model));
  triples.sort((a, b) => byText(a.key.toLowerCase(), b.key.toLowerCase()) || byText(a.tread, b.tread));

  const { defaults, singles } = buildCatalogueSuggestions(active);

  return {
    defaults,
    singles,
    yards: yardChoices(yards).map((c) => c.label),
    suppliers: supplierChoices(suppliers).map((c) => c.label),
    sizes: extend(TYRE_SIZES, 'tyreSize', normaliseTyreSize),
    types: extend(TYRE_TYPES, 'tyreType'),
    applications: extend(TYRE_APPLICATIONS, 'tyreApplication'),
    grades: extend(TYRE_GRADES, 'tyreGrade'),
    manufacturers,
    pairs,
    triples,
    models: uniqueBy(pairs.map((p) => p.model)),
    treads: uniqueBy(triples.map((t) => t.tread))
  };
}

/**
 * The catalogue auto-fill, precomputed with the shared rule.
 *   singles   [{ key: 'manufacturer|model', tread }] — models with exactly one tread
 *   defaults  [{ key, tyreSize, tyreType, … }] — one row per
 *               'manufacturer|model|tread'           (tread '' = the blank-tread rows)
 *               'manufacturer|model|tread|size'      (size normalised like normaliseTyreSize)
 *             with null where the matching entries disagree (no suggestion).
 * Keys are matched in Excel with MATCH (case-insensitive) against the row's
 * trimmed cells, so any spelling of a name finds its row.
 */
export function buildCatalogueSuggestions(active = []) {
  const groups = new Map();
  for (const e of active) {
    const m = String(e?.manufacturerName ?? '').trim();
    const mo = String(e?.modelName ?? '').trim();
    if (!m || !mo) continue;
    const k = `${normaliseCatalogueValue(m)}|${normaliseCatalogueValue(mo)}`;
    if (!groups.has(k)) groups.set(k, { m, mo, rows: [] });
    groups.get(k).rows.push(e);
  }

  const singles = [];
  const defaults = [];
  const row = (key, d) => ({
    key,
    ...d,
    // The size the dropdown lists (vocabulary spelling) when it is one of them.
    tyreSize: d.tyreSize === null ? null : (canonicalTyreSize(d.tyreSize, TYRE_SIZES) || d.tyreSize)
  });

  for (const { m, mo, rows } of groups.values()) {
    const only = singleCatalogueTread(rows);
    if (only) singles.push({ key: `${m}|${mo}`, tread: only });

    const treads = uniqueCatalogueValues(rows, 'treadPatternCode');
    if (rows.some((r) => !String(r?.treadPatternCode ?? '').trim())) treads.push('');
    for (const t of treads) {
      const byTread = narrowCatalogueRows(rows, { tread: t });
      if (!byTread.length) continue;
      defaults.push(row(`${m}|${mo}|${t}`, catalogueDefaultsFor(byTread)));
      for (const size of uniqueCatalogueValues(byTread, 'tyreSize').filter((x, i, a) =>
        a.findIndex((y) => normaliseTyreSize(y) === normaliseTyreSize(x)) === i)) {
        const bySize = narrowCatalogueRows(rows, { tread: t, size });
        defaults.push(row(`${m}|${mo}|${t}|${normaliseTyreSize(size)}`, catalogueDefaultsFor(bySize)));
      }
    }
  }
  return { defaults, singles };
}

// Lists-sheet layout: one source per column, header in row 1, values from row 2.
const LIST_COLUMNS = [
  { id: 'yards', header: 'Yard' },
  { id: 'suppliers', header: 'Supplier' },
  { id: 'sizes', header: 'Size' },
  { id: 'types', header: 'Type' },
  { id: 'applications', header: 'Application' },
  { id: 'grades', header: 'Grade' },
  { id: 'manufacturers', header: 'Manufacturer' },
  { id: 'pairManufacturer', header: 'Model: manufacturer' },
  { id: 'pairModel', header: 'Model' },
  { id: 'tripleKey', header: 'Tread: manufacturer|model' },
  { id: 'tripleTread', header: 'Tread pattern' },
  { id: 'models', header: 'All models' },
  { id: 'treads', header: 'All tread patterns' },
  // Catalogue auto-fill lookup (see buildCatalogueSuggestions). Each column the
  // suggestion formulas read has a workbook NAME: Excel forbids a shared formula
  // from referring to another sheet directly ([MS-XLSB] SharedParsedFormula: no
  // PtgRef3d/PtgArea3d) and "repairs" the file by deleting every such formula,
  // but a defined name is allowed — and reads better in the formula bar.
  { id: 'defKey', header: 'Suggestion key', name: 'PT_SuggestKey' },
  { id: 'def:tyreSize', header: 'Size', name: 'PT_SuggestSize' },
  { id: 'def:tyreType', header: 'Type', name: 'PT_SuggestType' },
  { id: 'def:tyreApplication', header: 'Application', name: 'PT_SuggestApplication' },
  { id: 'def:tyreGrade', header: 'Grade', name: 'PT_SuggestGrade' },
  { id: 'def:initialTyreTreadDepth', header: 'Initial tread', name: 'PT_SuggestInitialTread' },
  { id: 'def:minimumTreadForRetread', header: 'Min tread for retread', name: 'PT_SuggestMinTread' },
  { id: 'def:manufacturerExpectedMileage', header: 'Expected mileage', name: 'PT_SuggestMileage' },
  { id: 'singleKey', header: 'Single tread: manufacturer|model', name: 'PT_SingleTreadKey' },
  { id: 'singleTread', header: 'Single tread', name: 'PT_SingleTread' }
];

// Tyre fields the template suggests from the catalogue (Tread pattern aside).
const SUGGESTED_FIELDS = ['tyreSize', 'tyreType', 'tyreApplication', 'tyreGrade',
  'initialTyreTreadDepth', 'minimumTreadForRetread', 'manufacturerExpectedMileage'];
const NUMERIC_SUGGESTIONS = new Set(['initialTyreTreadDepth', 'minimumTreadForRetread', 'manufacturerExpectedMileage']);

function listValues(lists) {
  return {
    yards: lists.yards,
    suppliers: lists.suppliers,
    sizes: lists.sizes,
    types: lists.types,
    applications: lists.applications,
    grades: lists.grades,
    manufacturers: lists.manufacturers,
    pairManufacturer: lists.pairs.map((p) => p.manufacturer),
    pairModel: lists.pairs.map((p) => p.model),
    tripleKey: lists.triples.map((t) => t.key),
    tripleTread: lists.triples.map((t) => t.tread),
    models: lists.models,
    treads: lists.treads,
    defKey: lists.defaults.map((d) => d.key),
    ...Object.fromEntries(SUGGESTED_FIELDS.map((f) => [`def:${f}`, lists.defaults.map((d) => d[f])])),
    singleKey: lists.singles.map((x) => x.key),
    singleTread: lists.singles.map((x) => x.tread)
  };
}

// ---------------------------------------------------------------------------
// Catalogue auto-fill formulas
// ---------------------------------------------------------------------------

/**
 * One formula per suggested column, written for row 2 (relative refs shift down).
 * Every lookup is IFERROR-guarded, and an empty table cell yields "" (never 0).
 * Lookup tables are referenced by workbook name (see LIST_COLUMNS), never as
 * PT_Lists!…: these are written as shared formulas, which may not cross sheets.
 * @returns {Array<{key, formula}>}
 */
export function suggestionFormulas(columns, values) {
  const at = (key) => columns.findIndex((c) => c.key === key);
  if (at('tyreBrand') < 0 || at('tyreModel') < 0) return [];
  const ref = (key) => `$${colLetter(at(key))}2`;
  const E = ref('tyreBrand');
  const F = ref('tyreModel');
  const noSpec = `OR(TRIM(${E})="",TRIM(${F})="")`;
  const k2 = `TRIM(${E})&"|"&TRIM(${F})`;
  const k3 = at('tyreTreadPattern') >= 0 ? `${k2}&"|"&TRIM(${ref('tyreTreadPattern')})` : `${k2}&"|"`;
  const k4 = at('tyreSize') >= 0 ? `${k3}&"|"&SUBSTITUTE(SUBSTITUTE(LOWER(${ref('tyreSize')})," ",""),"/r","r")` : null;
  const keys = nameOf('defKey', values);

  const lookup = (field, key, numeric) => {
    const r = nameOf(`def:${field}`, values);
    const hit = `INDEX(${r},MATCH(${key},${keys},0))`;
    return numeric ? `IF(${hit}="","",${hit})` : `${hit}&""`;
  };
  // Size-narrowed first (when the row has a size), then tread-narrowed; a key with
  // no entry falls through, a key whose entries disagree gives "" (no suggestion).
  const chain = (field, numeric, fallback) => {
    const tail = `IFERROR(${lookup(field, k3, numeric)},${fallback})`;
    return k4 && field !== 'tyreSize' ? `IFERROR(${lookup(field, k4, numeric)},${tail})` : tail;
  };

  const out = [];
  const singles = nameOf('singleKey', values);
  if (singles && at('tyreTreadPattern') >= 0) {
    out.push({
      key: 'tyreTreadPattern',
      formula: `IF(${noSpec},"",IFERROR(INDEX(${nameOf('singleTread', values)},MATCH(${k2},${singles},0))&"",""))`
    });
  }
  for (const field of SUGGESTED_FIELDS) {
    if (at(field) < 0) continue;
    if (field === 'tyreGrade') {
      // The form's default grade is "New"; a catalogue grade replaces it.
      const ID = at('externalTyreId') >= 0 ? ref('externalTyreId') : E;
      const blankRow = `AND(TRIM(${ID})="",TRIM(${E})="")`;
      out.push({
        key: field,
        formula: keys
          ? `IF(${blankRow},"",IF(${noSpec},"New",${chain(field, false, '"New"')}))`
          : `IF(${blankRow},"","New")`
      });
      continue;
    }
    if (!keys) continue;
    out.push({ key: field, formula: `IF(${noSpec},"",${chain(field, NUMERIC_SUGGESTIONS.has(field), '""')})` });
  }
  return out;
}

/** Absolute range of a Lists column, e.g. Lists!$A$2:$A$7 (null when empty). */
function rangeOf(id, values) {
  const i = LIST_COLUMNS.findIndex((c) => c.id === id);
  const n = values[id].length;
  if (i < 0 || !n) return null;
  const L = colLetter(i);
  return `${sheetRef(LISTS_SHEET)}!$${L}$2:$${L}$${n + 1}`;
}

/** The workbook name of a Lists column (null when the column is empty). */
function nameOf(id, values) {
  const c = LIST_COLUMNS.find((x) => x.id === id);
  if (!c?.name) throw new Error(`Lists column ${id} has no workbook name.`);
  return rangeOf(id, values) ? c.name : null;
}

/** Workbook names for every non-empty named Lists column. */
function definedNamesFor(values) {
  return LIST_COLUMNS
    .filter((c) => c.name && rangeOf(c.id, values))
    .map((c) => ({ name: c.name, ref: rangeOf(c.id, values), hidden: true }));
}

function firstCellAbove(id) {
  const L = colLetter(LIST_COLUMNS.findIndex((c) => c.id === id));
  return `${sheetRef(LISTS_SHEET)}!$${L}$1`;
}

// ---------------------------------------------------------------------------
// Validations
// ---------------------------------------------------------------------------

/**
 * Dependent-list formula: the slice of `valueId` whose `keyId` equals `keyExpr`,
 * else every value in `allId`. Relative row refs are written for row 2 and Excel
 * shifts them down the column. (MATCH/COUNTIF treat * ? ~ in a name as
 * wildcards; tyre names don't use them, and the list is advisory anyway.)
 */
function dependentList(keyExpr, keyId, valueId, allId, values, blankTest) {
  const keys = rangeOf(keyId, values);
  const all = rangeOf(allId, values);
  if (!keys || !all) return null;
  const offsetCol = LIST_COLUMNS.findIndex((c) => c.id === valueId) - LIST_COLUMNS.findIndex((c) => c.id === keyId);
  return `IF(OR(${blankTest},COUNTIF(${keys},${keyExpr})=0),${all},` +
    `OFFSET(${firstCellAbove(keyId)},MATCH(${keyExpr},${keys},0),${offsetCol},COUNTIF(${keys},${keyExpr}),1))`;
}

function validationsFor(columns, values) {
  const last = TEMPLATE_ROWS + 1;
  const colOf = (key) => columns.findIndex((c) => c.key === key);
  const L = (key) => colLetter(colOf(key));
  const out = [];

  for (const [i, c] of columns.entries()) {
    const col = colLetter(i);
    const sqref = `${col}2:${col}${last}`;
    const list = (id, errorStyle, extra = {}) => {
      const r = rangeOf(id, values);
      if (r) out.push({ sqref, type: 'list', formula1: r, errorStyle, promptTitle: c.label, ...extra });
    };

    switch (c.kind) {
      case 'yard':
        list('yards', 'stop', {
          prompt: GUIDANCE.yardId,
          errorTitle: 'Unknown yard', error: 'Pick a yard from the list (Setup → Yards), or leave the cell blank.'
        });
        break;
      case 'supplier':
        list('suppliers', 'stop', {
          prompt: GUIDANCE.supplierId,
          errorTitle: 'Unknown supplier', error: 'Pick a supplier from the list. New suppliers are added under Setup → Suppliers.'
        });
        break;
      case 'select': {
        const id = { tyreSize: 'sizes', tyreType: 'types', tyreApplication: 'applications', tyreGrade: 'grades' }[c.key];
        if (!id) break;
        const open = c.key === 'tyreSize';
        list(id, open ? 'warning' : 'stop', {
          prompt: GUIDANCE[c.key],
          errorTitle: open ? 'Size not in the list' : `Pick a ${c.label.toLowerCase()}`,
          error: open ? 'This size is not in the list. Choose Yes to keep it anyway.' : `Choose a ${c.label.toLowerCase()} from the list.`
        });
        break;
      }
      case 'typeahead:manufacturer':
        list('manufacturers', 'information', {
          prompt: GUIDANCE.tyreBrand,
          errorTitle: 'Not in your catalogue', error: 'This manufacturer is not in your tyre catalogue. It will be checked when you import.'
        });
        break;
      case 'typeahead:model': {
        const m = `$${L('tyreBrand')}2`;
        const f = colOf('tyreBrand') >= 0
          ? dependentList(m, 'pairManufacturer', 'pairModel', 'models', values, `${m}=""`)
          : rangeOf('models', values);
        if (f) out.push({
          sqref, type: 'list', formula1: f, errorStyle: 'information', promptTitle: c.label, prompt: GUIDANCE.tyreModel,
          errorTitle: 'Not in your catalogue', error: 'This model is not in your tyre catalogue for that manufacturer. It will be checked when you import.'
        });
        break;
      }
      case 'typeahead:tread': {
        const m = `$${L('tyreBrand')}2`;
        const mo = `$${L('tyreModel')}2`;
        const f = colOf('tyreBrand') >= 0 && colOf('tyreModel') >= 0
          ? dependentList(`${m}&"|"&${mo}`, 'tripleKey', 'tripleTread', 'treads', values, `${mo}=""`)
          : rangeOf('treads', values);
        if (f) out.push({
          sqref, type: 'list', formula1: f, errorStyle: 'information', promptTitle: c.label, prompt: GUIDANCE.tyreTreadPattern,
          errorTitle: 'Not in your catalogue', error: 'This tread pattern is not in your tyre catalogue for that model. It will be checked when you import.'
        });
        break;
      }
      case 'decimal':
      case 'integer':
        out.push({
          sqref, type: c.kind === 'integer' ? 'whole' : 'decimal', operator: 'greaterThanOrEqual', formula1: '0',
          errorStyle: 'stop', promptTitle: c.label, prompt: GUIDANCE[c.key] || '',
          errorTitle: 'Number needed',
          error: c.kind === 'integer' ? `${c.label}: enter a whole number of 0 or more.` : `${c.label}: enter a number of 0 or more.`
        });
        break;
      case 'date':
        // 1 = 1900-01-01, 401768 = 2999-12-31 (Excel serials).
        out.push({
          sqref, type: 'date', operator: 'between', formula1: '1', formula2: '401768',
          errorStyle: 'stop', promptTitle: c.label, prompt: GUIDANCE[c.key] || 'Enter a date.',
          errorTitle: 'Date needed', error: 'Enter a date, e.g. 2026-04-08.'
        });
        break;
      default:
        if (c.key === 'externalTyreId') {
          // A duplicate ID in the sheet would make the second row overwrite the
          // first on import. COUNTIF is case-insensitive — the importer's rule too.
          out.push({
            sqref, type: 'custom', formula1: `COUNTIF($${col}$2:$${col}$${last},${col}2)<=1`,
            errorStyle: 'stop', promptTitle: c.label, prompt: GUIDANCE.externalTyreId,
            errorTitle: 'Duplicate Tyre ID', error: 'This Tyre ID is already used in this sheet. Each tyre needs its own ID.'
          });
        }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Workbook
// ---------------------------------------------------------------------------

function choicesText(c, lists) {
  const n = (arr, one) => (arr.length ? `${arr.length} ${one}${arr.length === 1 ? '' : 's'} from your account` : `none set up yet`);
  switch (c.kind) {
    case 'yard': return `Dropdown: ${n(lists.yards, 'yard')}`;
    case 'supplier': return `Dropdown: ${n(lists.suppliers, 'supplier')}`;
    case 'typeahead:manufacturer': return lists.manufacturers.length ? `Dropdown: ${lists.manufacturers.length} from your tyre catalogue, or type a new one` : 'Free text (your tyre catalogue is empty)';
    case 'typeahead:model': return lists.models.length ? 'Dropdown filtered by manufacturer, or type a new one' : 'Free text';
    case 'typeahead:tread': return lists.treads.length ? 'Dropdown filtered by manufacturer and model, or type a new one' : 'Free text';
    case 'select': {
      const arr = { tyreSize: lists.sizes, tyreType: lists.types, tyreApplication: lists.applications, tyreGrade: lists.grades }[c.key] || [];
      return c.key === 'tyreSize' ? `Dropdown (${arr.length} sizes); others allowed after a warning` : `Dropdown: ${arr.join(', ')}`;
    }
    case 'decimal': return 'Number, 0 or more';
    case 'integer': return 'Whole number, 0 or more';
    case 'date': return 'Date';
    default: return c.key === 'externalTyreId' ? 'Text, unique in the sheet' : 'Text';
  }
}

/**
 * The workbook description for writeXlsx().
 * @param {object} src
 * @param {object[]} [src.yards]
 * @param {object[]} [src.suppliers]
 * @param {object[]} [src.catalogue]
 * @param {string}   [src.customerName]
 * @param {string}   [src.generatedOn]  'YYYY-MM-DD'
 * @param {string[]} [src.unavailable]  sources that could not be loaded (named in the sheet)
 */
export function buildTemplateWorkbook({ yards = [], suppliers = [], catalogue = [], customerName = '', generatedOn = '', unavailable = [] } = {}) {
  const columns = templateColumns();
  const lists = buildTemplateLists({ yards, suppliers, catalogue });
  const values = listValues(lists);

  // --- Tyres ---------------------------------------------------------------
  const styleFor = (c) => {
    if (c.kind === 'date') return STYLE.DATE;
    if (c.key === 'purchaseCost') return STYLE.MONEY;
    // IDs and codes stay text: Excel must not turn "0012" into 12 or "1E5" into 100000.
    if (['externalTyreId', 'tyreRFID', 'tyreSerialNumber', 'invoiceNo'].includes(c.key)) return STYLE.TEXT;
    return undefined;
  };
  const tyres = {
    name: TEMPLATE_SHEET,
    selected: true,
    freezeRows: 1,
    rowHeights: { 0: 32 },
    cols: columns.map((c) => ({ width: c.width, style: styleFor(c) })),
    rows: [columns.map((c) => ({ v: c.header, s: c.required ? STYLE.HEADER_REQUIRED : STYLE.HEADER }))],
    validations: validationsFor(columns, values)
  };
  const suggestions = suggestionFormulas(columns, values);
  tyres.formulaColumns = suggestions.map((sg) => {
    const i = columns.findIndex((c) => c.key === sg.key);
    return { col: i, formula: sg.formula, fromRow: 1, toRow: TEMPLATE_ROWS, style: styleFor(columns[i]) };
  });
  // Grey a suggested cell while it still holds the formula; a typed value loses it.
  tyres.conditionalFormats = suggestions.map((sg) => {
    const L = colLetter(columns.findIndex((c) => c.key === sg.key));
    // Only a cell SHOWING a suggestion: an empty formula cell keeps the plain grid.
    return { sqref: `${L}2:${L}${TEMPLATE_ROWS + 1}`, formula: `AND(_xlfn.ISFORMULA(${L}2),${L}2<>"")`, format: 'suggestion' };
  });
  const suggestedKeys = new Set(suggestions.map((sg) => sg.key));

  // --- Instructions --------------------------------------------------------
  const intro = [
    [{ v: 'PressureTrack — tyre import template', s: STYLE.TITLE }],
    [{ v: [customerName ? `Prepared for ${customerName}` : '', generatedOn ? `on ${generatedOn}` : ''].filter(Boolean).join(' '), s: STYLE.MUTED }],
    [],
    [{ v: 'How to use it', s: STYLE.BOLD }],
    [{ v: `1. Fill in one tyre per row on the “${TEMPLATE_SHEET}” sheet. Only Tyre ID is required; leave anything you don’t know blank.`, s: STYLE.WRAP }],
    [{ v: '2. Use the dropdowns — they list your own yards, suppliers and tyre catalogue. Type a letter to jump through a long list.', s: STYLE.WRAP }],
    [{ v: '3. Save, then in PressureTrack go to Tyre Management → Setup → Import tyres and choose this file. The columns are recognised automatically.', s: STYLE.WRAP }],
    [{ v: '4. Review shows every row before anything is saved. A Tyre ID that already exists is updated or skipped — you choose.', s: STYLE.WRAP }],
    [{ v: 'Grey cells are suggestions from your tyre catalogue: pick a manufacturer and model and the specification fills in, as on the New tyre form. Type over a grey cell to use your own value (to bring a suggestion back, copy that cell from another row). For a tyre that already exists, a suggestion never replaces a value it already has.', s: STYLE.WRAP }],
    [{ v: 'Dropdowns reflect your account on the day this template was downloaded. Download a fresh copy after adding yards, suppliers or catalogue entries.', s: STYLE.MUTED }]
  ];
  if (unavailable.length) {
    intro.push([{ v: `Not included (could not be loaded): ${unavailable.join(', ')}. Those columns accept typed values.`, s: STYLE.MUTED }]);
  }
  intro.push([]);
  const tableHead = ['Column', 'Required', 'What to enter', 'Choices'].map((v) => ({ v, s: STYLE.TABLE_HEAD }));
  const tableRows = columns.map((c) => [
    { v: c.header.replace(/ \*$/, ''), s: STYLE.BOLD },
    { v: c.required ? 'Yes' : '', s: STYLE.WRAP },
    { v: GUIDANCE[c.key] || `As on the tyre form (${c.label}).`, s: STYLE.WRAP },
    { v: choicesText(c, lists) + (suggestedKeys.has(c.key) ? (c.key === 'tyreGrade' ? ' · suggested from your catalogue, else “New”' : ' · suggested from your catalogue') : ''), s: STYLE.WRAP }
  ]);
  const instructions = {
    name: INSTRUCTIONS_SHEET,
    cols: [{ width: 34 }, { width: 10 }, { width: 70 }, { width: 48 }],
    rows: [...intro, tableHead, ...tableRows],
    merges: intro.map((r, i) => (r.length ? `A${i + 1}:D${i + 1}` : null)).filter(Boolean)
  };

  // --- Lists (hidden) ------------------------------------------------------
  const height = Math.max(0, ...LIST_COLUMNS.map((c) => values[c.id].length));
  const listRows = [LIST_COLUMNS.map((c) => ({ v: c.header, s: STYLE.TABLE_HEAD }))];
  for (let r = 0; r < height; r++) {
    // Text typed as text, so a numeric-looking size or code stays exactly as
    // listed; the suggested numbers (tread, mileage) stay numbers.
    listRows.push(LIST_COLUMNS.map((c) => {
      const v = values[c.id][r];
      if (v === undefined || v === null || v === '') return null;
      return typeof v === 'number' ? { v } : { v: String(v), s: STYLE.TEXT };
    }));
  }
  const listsSheet = {
    name: LISTS_SHEET,
    hidden: true,
    cols: LIST_COLUMNS.map(() => ({ width: 26 })),
    rows: listRows
  };

  return {
    title: 'PressureTrack tyre import template',
    creator: 'PressureTrack',
    sheets: [tyres, instructions, listsSheet],
    definedNames: definedNamesFor(values),
    columns,
    lists
  };
}

/** e.g. "PressureTrack tyre import template - Demo Transport - 2026-10-02.xlsx" */
export function templateFileName({ customerName = '', generatedOn = '' } = {}) {
  const safe = (s) => String(s).replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${['PressureTrack tyre import template', safe(customerName), generatedOn].filter(Boolean).join(' - ')}.xlsx`;
}
